import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { Languages } from 'lucide-react';
import { useState } from 'react';

interface LanguageSwitcherProps {
    variant?: 'dropdown' | 'select' | 'buttons';
    className?: string;
    showLabel?: boolean;
}

export function LanguageSwitcher({ variant = 'dropdown', className = '', showLabel = false }: LanguageSwitcherProps) {
    const { currentLanguage, availableLanguages, changeLanguage, getLanguageName, t } = useTranslation();
    const [isChanging, setIsChanging] = useState(false);

    const handleLanguageChange = async (newLanguage: string) => {
        if (newLanguage === currentLanguage || isChanging) return;

        setIsChanging(true);
        try {
            await changeLanguage(newLanguage);
        } catch (error) {
            console.error('Failed to change language:', error);
        } finally {
            setIsChanging(false);
        }
    };

    const getFlagEmoji = (lang: string) => {
        const flags: Record<string, string> = {
            en: '🇺🇸',
            pt: '🇵🇹',
            de: '🇩🇪',
            nl: '🇳🇱',
        };
        return flags[lang] || '🌐';
    };

    if (variant === 'dropdown') {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className={className} disabled={isChanging} aria-label={t('language')}>
                        <Languages className="h-4 w-4" aria-hidden="true" />
                        {showLabel && (
                            <span className="ml-2">
                                {getFlagEmoji(currentLanguage)} {getLanguageName(currentLanguage)}
                            </span>
                        )}
                        {!showLabel && <span className="ml-1">{getFlagEmoji(currentLanguage)}</span>}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {availableLanguages.map((lang: string) => (
                        <DropdownMenuItem
                            key={lang}
                            onClick={() => handleLanguageChange(lang)}
                            disabled={lang === currentLanguage || isChanging}
                            className={lang === currentLanguage ? 'bg-accent' : ''}
                        >
                            <span className="mr-2">{getFlagEmoji(lang)}</span>
                            {getLanguageName(lang)}
                            {lang === currentLanguage && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    if (variant === 'select') {
        return (
            <Select value={currentLanguage} onValueChange={handleLanguageChange} disabled={isChanging}>
                <SelectTrigger className={className} aria-label={t('language')}>
                    <SelectValue>
                        <span className="flex items-center">
                            <span className="mr-2">{getFlagEmoji(currentLanguage)}</span>
                            {showLabel ? getLanguageName(currentLanguage) : currentLanguage.toUpperCase()}
                        </span>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {availableLanguages.map((lang: string) => (
                        <SelectItem key={lang} value={lang}>
                            <span className="flex items-center">
                                <span className="mr-2">{getFlagEmoji(lang)}</span>
                                {getLanguageName(lang)}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    if (variant === 'buttons') {
        return (
            <div className={`flex gap-1 ${className}`}>
                {availableLanguages.map((lang: string) => (
                    <Button
                        key={lang}
                        variant={lang === currentLanguage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleLanguageChange(lang)}
                        disabled={isChanging}
                        className="min-w-[60px]"
                        aria-pressed={lang === currentLanguage}
                        aria-label={getLanguageName(lang)}
                    >
                        {getFlagEmoji(lang)} {showLabel ? getLanguageName(lang) : lang.toUpperCase()}
                    </Button>
                ))}
            </div>
        );
    }

    return null;
}

export default LanguageSwitcher;
